import { useEffect, useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import StatusPageService from 'services/status-page/StatusPage';
import TokenService from 'services/token/Token';

const POLL_INTERVAL = 3 * 60 * 1000; // 3 minutes
const TOAST_QUEUE_INTERVAL = 60 * 1000; // 1 minute between toasts

const getSeenKey = (event) => `status_seen_${event._id}_${event.updatedAt}`;
const isSeen = (event) => !!sessionStorage.getItem(getSeenKey(event));
const markSeen = (event) => sessionStorage.setItem(getSeenKey(event), 'true');

const useStatusNotifications = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queueTimersRef = useRef([]);
  const queuedEventIdsRef = useRef(new Set());

  const clearQueueTimers = useCallback(() => {
    queueTimersRef.current.forEach(clearTimeout);
    queueTimersRef.current = [];
    queuedEventIdsRef.current.clear();
  }, []);

  const enqueueEvents = useCallback(
    (events) => {
      const unseen = events.filter((event) => !isSeen(event) && !queuedEventIdsRef.current.has(getSeenKey(event)));
      if (!unseen.length) return;

      unseen.forEach((event, index) => {
        const seenKey = getSeenKey(event);
        queuedEventIdsRef.current.add(seenKey);

        const timer = setTimeout(() => {
          enqueueSnackbar({
            autoHideDuration: 15 * 1000,
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            variant: event.type === 'incident' ? 'incident' : 'maintenance',
            event,
          });
          markSeen(event);
          queuedEventIdsRef.current.delete(seenKey);
        }, index * TOAST_QUEUE_INTERVAL);

        queueTimersRef.current.push(timer);
      });
    },
    [enqueueSnackbar]
  );

  const fetchAndEnqueue = useCallback(async () => {
    try {
      const user = TokenService.getData();
      if (!user?.data) return;

      const events = await StatusPageService.getActiveEvents();
      enqueueEvents(events);
    } catch (err) {
      console.error('useStatusNotifications:', err);
    }
  }, [enqueueEvents]);

  useEffect(() => {
    fetchAndEnqueue();

    const pollTimer = setInterval(fetchAndEnqueue, POLL_INTERVAL);

    return () => {
      clearInterval(pollTimer);
      clearQueueTimers();
    };
  }, [fetchAndEnqueue, clearQueueTimers]);
};

export default useStatusNotifications;
